import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Slide,
  Container,
  Chip,
  Avatar,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Settings,
  History,
  Folder,
  ArrowBack,
  Storage,
} from '@mui/icons-material';
import { Project } from '../types';
import { currentChangesAPI, gitLogAPI } from '../services/api';

interface RobotDashboardProps {
  robot: Project;
  onBack: () => void;
  onNavigateToParameters: () => void;
  onNavigateToSystemParametersGit: () => void;
  onNavigateToApplicationFolders: () => void;
  onNavigateToCurrentChanges: () => void;
}

const RobotDashboard: React.FC<RobotDashboardProps> = ({
  robot,
  onBack,
  onNavigateToParameters,
  onNavigateToSystemParametersGit,
  onNavigateToApplicationFolders,
  onNavigateToCurrentChanges,
}) => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState({
    currentChanges: {
      totalFiles: 0,
      modified: 0,
      newFiles: 0
    },
    systemParameters: {
      commits: 0,
      branches: 0
    },
    applicationFolders: {
      appFolders: 0,
      activeVersions: 0
    }
  });
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Check if projectName is an IP address
      const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^::1$|^localhost$/.test(robot.projectName);
      
      console.log('🤖 Robot Dashboard Debug:', {
        robotProjectName: robot.projectName,
        isIpAddress,
        robotObject: robot
      });

      // Fetch current changes data
      let currentChangesResponse;
      if (isIpAddress) {
        // For IP addresses, use tracked files API
        console.log('🔍 Calling tracked files API for IP:', robot.projectName);
        try {
          currentChangesResponse = await currentChangesAPI.getTrackedFilesByIpAddress(robot.projectName, true);
          console.log('✅ Tracked files API response:', currentChangesResponse);
        } catch (apiError) {
          console.error('❌ Tracked files API error:', apiError);
          throw apiError;
        }
      } else {
        // For project names, use regular current changes API
        console.log('🔍 Calling current changes API for project:', robot.projectName);
        try {
          currentChangesResponse = await currentChangesAPI.getCurrentChanges(robot.projectName, {
            branchName: 'main'
          });
          console.log('✅ Current changes API response:', currentChangesResponse);
        } catch (apiError) {
          console.error('❌ Current changes API error:', apiError);
          throw apiError;
        }
      }

      // Fetch system parameters data
      let systemParamsResponse;
      if (isIpAddress) {
        console.log('🔍 Calling system parameters API for IP:', robot.projectName);
        try {
          systemParamsResponse = await gitLogAPI.getSystemParametersByIpAddress(robot.projectName, true);
          console.log('✅ System parameters API response:', systemParamsResponse);
        } catch (apiError) {
          console.error('❌ System parameters API error:', apiError);
          throw apiError;
        }
      } else {
        console.log('🔍 Calling system parameters history API for project:', robot.projectName);
        try {
          systemParamsResponse = await gitLogAPI.getSystemParametersHistory(robot.projectName, {
            page: 1,
            limit: 50
          });
          console.log('✅ System parameters history API response:', systemParamsResponse);
        } catch (apiError) {
          console.error('❌ System parameters history API error:', apiError);
          throw apiError;
        }
      }

      // Fetch application folders data
      const appFoldersResponse = await gitLogAPI.getApplicationFolders(robot.projectName);

      // Process current changes data
      let currentChangesData = {
        totalFiles: 0,
        modified: 0,
        newFiles: 0
      };

      if (currentChangesResponse.success && currentChangesResponse.data) {
        let changes: any[] = [];
        
        console.log('🔍 Current Changes Response:', {
          isIpAddress,
          response: currentChangesResponse,
          data: currentChangesResponse.data
        });
        
        if (isIpAddress) {
          // Handle tracked files API response structure
          if (Array.isArray(currentChangesResponse.data)) {
            // Extract files from each project/robot entry
            currentChangesResponse.data.forEach((entry: any) => {
              console.log('📁 Processing entry:', entry);
              if (entry.files && Array.isArray(entry.files)) {
                console.log('📄 Found files:', entry.files.length);
                changes = changes.concat(entry.files);
              }
            });
          }
        } else {
          // Handle regular current changes API response
          changes = currentChangesResponse.data.changes || [];
        }
        
        console.log('📊 Final changes array:', changes);
        
        currentChangesData = {
          totalFiles: changes.length,
          modified: changes.filter((change: any) => change.changeType === 'modified').length,
          newFiles: changes.filter((change: any) => change.changeType === 'added').length
        };
        
        console.log('📈 Current Changes Data:', currentChangesData);
      }

      // Process system parameters data
      let systemParamsData = {
        commits: 0,
        branches: 0
      };

      if (systemParamsResponse.success && systemParamsResponse.data) {
        console.log('🔍 System Parameters Response:', {
          isIpAddress,
          response: systemParamsResponse,
          data: systemParamsResponse.data
        });
        
        if (isIpAddress) {
          // Handle IP-based system parameters response
          if (Array.isArray(systemParamsResponse.data)) {
            systemParamsData = {
              commits: systemParamsResponse.data.length,
              branches: 1 // For IP views, we typically have one branch (main)
            };
          } else {
            systemParamsData = {
              commits: systemParamsResponse.data.recentCommits?.length || 0,
              branches: systemParamsResponse.data.branchInfo?.branches?.length || 1
            };
          }
        } else {
          // Handle regular project-based system parameters response
          systemParamsData = {
            commits: systemParamsResponse.data.recentCommits?.length || 0,
            branches: systemParamsResponse.data.branchInfo?.branches?.length || 0
          };
        }
        
        console.log('📈 System Parameters Data:', systemParamsData);
      }

      // Process application folders data
      let appFoldersData = {
        appFolders: 0,
        activeVersions: 0
      };

      if (appFoldersResponse.success && appFoldersResponse.data) {
        appFoldersData = {
          appFolders: appFoldersResponse.data.folders?.length || 0,
          activeVersions: appFoldersResponse.data.folders?.filter((folder: any) => folder.isActive).length || 0
        };
      }

      const finalDashboardData = {
        currentChanges: currentChangesData,
        systemParameters: systemParamsData,
        applicationFolders: appFoldersData
      };
      
      console.log('🎯 Final Dashboard Data:', finalDashboardData);
      
      setDashboardData(finalDashboardData);

    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        robotProjectName: robot.projectName,
        isIpAddress: /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^::1$|^localhost$/.test(robot.projectName)
      });
    } finally {
      setLoading(false);
    }
  }, [robot.projectName]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const renderOptionCard = (
    title: string,
    description: string,
    icon: React.ReactNode,
    onClick: () => void,
    delay: number,
    cardKey: string
  ) => (
    <Slide direction="up" in timeout={800 + delay} mountOnEnter unmountOnExit>
      <Card
        sx={{
          height: 280,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 4,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          transform: hoveredCard === cardKey ? 'translateY(-8px) scale(1.02)' : 'translateY(0)',
          boxShadow: hoveredCard === cardKey
            ? '0 20px 40px rgba(0,0,0,0.15)'
            : '0 8px 32px rgba(0,0,0,0.1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 1)',
          },
        }}
        onClick={onClick}
        onMouseEnter={() => setHoveredCard(cardKey)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        <CardContent sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          textAlign: 'center'
        }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              mb: 3,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              transform: hoveredCard === cardKey ? 'scale(1.1)' : 'scale(1)',
              transition: 'all 0.3s ease',
            }}
          >
            {icon}
          </Avatar>

          <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a202c', mb: 2 }}>
            {title}
          </Typography>

          <Typography variant="body2" sx={{ color: '#4a5568', mb: 0, lineHeight: 1.6 }}>
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Slide>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ color: 'white', mb: 2 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          Loading dashboard data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 2,
            '&:hover': {
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Back to Robot Options
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 60,
              height: 60,
              mr: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <Storage sx={{ fontSize: 30, color: 'white' }} />
          </Avatar>
          <Box>
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              {robot.customName || (robot.robotNames && robot.robotNames.length > 0 ? robot.robotNames[0] : robot.projectName)}
            </Typography>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Robot Configuration & Monitoring
            </Typography>
            {robot.ipAddress && (
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                IP Address: {robot.ipAddress}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      </Box>

      {/* Cards Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4,
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        {/* Card 1: Current Changes/Deviation from Verified Parameters */}
        {renderOptionCard(
          'Critical Files',
          'View all critical files in the project with their status and modifications',
          <Settings sx={{ fontSize: 36, color: 'white' }} />,
          onNavigateToCurrentChanges,
          0,
          'current-changes'
        )}

        {/* Card 2: Change History of Parameters/Environment Variables */}
        {renderOptionCard(
          'System Parameters',
          'Change history of system_parameters folder that affects robot behavior',
          <History sx={{ fontSize: 36, color: 'white' }} />,
          onNavigateToSystemParametersGit,
          200,
          'system-params'
        )}

        {/* Card 3: Change History of Application Folders */}
        {renderOptionCard(
          'Application Folders',
          'Change history of application folders - ensure correct versions are running',
          <Folder sx={{ fontSize: 36, color: 'white' }} />,
          onNavigateToApplicationFolders,
          400,
          'app-folders'
        )}
      </Box>

      {/* Additional Info */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          Monitor robot configuration, parameters, and application versions to ensure system integrity
        </Typography>
      </Box>
    </Container>
  );
};

export default RobotDashboard;
